import React, { useMemo, useState } from 'react'
import { Spin, Table, Tag } from '@surecompanies/core_components'
import dayjs from 'dayjs'
import { CurrencyFormatter } from 'helpers/formatters'
import { TIMEZONE_PST } from 'helpers/datetime'
import { TimezoneSelect, TimezoneOption } from '../Utils/TimezoneSelect'
import {
  useVcnTransactionsQuery,
  useAchIstreamTransactionsQuery,
} from '../../../api/finance-api-client'
import { GetAchIstreamTransactionResponseDto } from 'enrollment-platform/pages/finance/api/dto/get-ach-istream-transaction.dto'
import { capitalize } from 'lodash'

interface TransactionTabProps {
  benefitId: string
  /** WEX merchant log id when the case uses a WEX VCN (EPS merged timeline). */
  merchantLogId?: string | null
}

interface UnifiedTransaction {
  id: string
  transaction_date: string
  transaction_amount: number
  status: string
  type: string
  source: 'VCN' | 'ACH'
  vcn_card?: string
}

const currencyFormatter = new CurrencyFormatter()

const STATUS_COLOR: Record<string, string> = {
  Authorized: 'blue',
  Pending: 'orange',
  Settled: 'green',
  Failed: 'red',
  Voided: 'red',
  Declined: 'red',
}

function normalizeAchTransactions(
  rows: GetAchIstreamTransactionResponseDto[]
): UnifiedTransaction[] {
  return rows.map((row) => ({
    id: row.id,
    transaction_date: row.transactionDate,
    transaction_amount: row.transactionAmountCents / 100,
    status: capitalize(row.transactionStatus),
    type: row.transactionType,
    source: 'ACH' as const,
  }))
}

export function TransactionTab({
  benefitId,
  merchantLogId = null,
}: TransactionTabProps) {
  const [tz, setTz] = useState<TimezoneOption>('local')

  const {
    data: vcnTransactions = [],
    isLoading: vcnLoading,
    isError: vcnError,
  } = useVcnTransactionsQuery(benefitId || null, merchantLogId)

  const {
    data: achRaw = [],
    isLoading: achLoading,
    isError: achError,
  } = useAchIstreamTransactionsQuery(benefitId || null)

  const isLoading = vcnLoading || achLoading
  const isError = vcnError && achError

  const mergedTransactions = useMemo<UnifiedTransaction[]>(() => {
    const vcnRows: UnifiedTransaction[] = vcnTransactions.map((t) => ({
      id: t.payment_transaction_id,
      transaction_date: t.transaction_date,
      transaction_amount: t.transaction_amount,
      status: t.status,
      type: t.type,
      source: 'VCN' as const,
      vcn_card: t.vcn_card,
    }))

    const achRows = normalizeAchTransactions(achRaw)

    return [...vcnRows, ...achRows].sort(
      (a, b) => Date.parse(b.transaction_date) - Date.parse(a.transaction_date)
    )
  }, [vcnTransactions, achRaw])

  const formatDate = (v: string) => {
    if (!v) return '—'
    const resolvedTz =
      tz === 'local'
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : TIMEZONE_PST
    return dayjs(v).tz(resolvedTz).format('MM-DD-YYYY hh:mm A')
  }

  const columns = useMemo(
    () => [
      {
        title: 'Date',
        dataIndex: 'transaction_date',
        key: 'transaction_date',
        render: (v: string) => formatDate(v),
        sorter: (a: UnifiedTransaction, b: UnifiedTransaction) =>
          Date.parse(a.transaction_date) - Date.parse(b.transaction_date),
        defaultSortOrder: 'descend' as const,
      },
      {
        title: 'Amount',
        dataIndex: 'transaction_amount',
        key: 'transaction_amount',
        align: 'right' as const,
        render: (v: number) =>
          v != null ? currencyFormatter.format(Number(v)) : '—',
        sorter: (a: UnifiedTransaction, b: UnifiedTransaction) =>
          a.transaction_amount - b.transaction_amount,
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        align: 'center' as const,
        render: (v: string) => (
          <Tag color={STATUS_COLOR[v] ?? 'default'}>{v ?? '—'}</Tag>
        ),
        sorter: (a: UnifiedTransaction, b: UnifiedTransaction) =>
          (a.status ?? '').localeCompare(b.status ?? ''),
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        align: 'center' as const,
        render: (v: string) => v ?? '—',
        sorter: (a: UnifiedTransaction, b: UnifiedTransaction) =>
          (a.type ?? '').localeCompare(b.type ?? ''),
      },
      {
        title: 'Source',
        dataIndex: 'source',
        key: 'source',
        align: 'center' as const,
        render: (v: 'VCN' | 'ACH') => (
          <Tag color={v === 'ACH' ? 'purple' : 'cyan'}>{v}</Tag>
        ),
        sorter: (a: UnifiedTransaction, b: UnifiedTransaction) =>
          a.source.localeCompare(b.source),
      },
      {
        title: 'Card',
        dataIndex: 'vcn_card',
        key: 'vcn_card',
        align: 'center' as const,
        render: (v: string | undefined) => v ?? '—',
        sorter: (a: UnifiedTransaction, b: UnifiedTransaction) =>
          (a.vcn_card ?? '').localeCompare(b.vcn_card ?? ''),
      },
    ],
    [tz]
  )

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    )
  }

  if (isError) {
    return (
      <div style={{ padding: 24, color: '#ff4d4f', textAlign: 'center' }}>
        Failed to load transactions.
      </div>
    )
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 12,
        }}
      >
        <span style={{ fontSize: 12, color: '#677786' }}>Timezone:</span>
        <TimezoneSelect value={tz} onChange={setTz} />
      </div>
      <Table<UnifiedTransaction>
        dataSource={mergedTransactions}
        columns={columns}
        rowKey="id"
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        locale={{ emptyText: 'No transactions found' }}
        size="small"
      />
    </>
  )
}
